import type { BulkTranscriptionTarget } from "./BulkTranscriptionModal";

/**
 * 一括文字起こしの対象を各一覧から集めるための共通ヘルパー。
 *
 * 共有ライブラリ / トークルーム / メッセンジャー / ショット編集 / 作品登録で
 * ファイルの型はバラバラだが、「音声か動画か」「再生時間はどこにあるか」の
 * 判定だけは揃えたいのでここに置く。
 */

/** 文字起こしの対象になるファイル種別（BE の対応形式と揃える） */
export const isTranscribableFileType = (
  fileType: string | null | undefined,
): boolean => fileType === "audio" || fileType === "video";

/**
 * `file_metadata`（JSON 文字列）から再生時間（秒）を取り出す。
 *
 * 変換 Lambda が probe した値が入っていれば一括モーダルの「合計の長さ」に
 * 使う。入っていなければ null（BE 側も未 probe のファイルは合計時間の
 * 判定から除外する）。
 *
 * Python の dict repr が入ってくることがあるのは `parseFileSizeBytes` と同じ。
 */
export const parseDurationSeconds = (
  metadata?: string | null,
): number | null => {
  if (!metadata) return null;
  try {
    const parsed = JSON.parse(metadata);
    if (typeof parsed === "object" && parsed !== null) {
      const duration = (parsed as Record<string, unknown>).duration;
      if (typeof duration === "number" && Number.isFinite(duration)) {
        return duration;
      }
      if (typeof duration === "string" && /^\d+(\.\d+)?$/.test(duration)) {
        return Number(duration);
      }
    }
  } catch {
    // JSON でない（Python dict repr 等）→ 正規表現で抽出
  }
  const match = metadata.match(/['"]?duration['"]?\s*:\s*(\d+(?:\.\d+)?)/);
  if (match) return Number(match[1]);
  return null;
};

/** 音声・動画だけを一括文字起こしの対象として抜き出す */
export const collectTranscriptionTargets = <T>(
  items: T[],
  toTarget: (
    item: T,
  ) => (BulkTranscriptionTarget & { fileType?: string | null }) | null,
): BulkTranscriptionTarget[] => {
  const seen = new Set<string>();
  const targets: BulkTranscriptionTarget[] = [];
  items.forEach((item) => {
    const target = toTarget(item);
    if (!target || !target.fileId) return;
    if (!isTranscribableFileType(target.fileType)) return;
    if (seen.has(target.fileId)) return;
    seen.add(target.fileId);
    targets.push({
      fileId: target.fileId,
      durationSeconds: target.durationSeconds ?? null,
    });
  });
  return targets;
};
